import React from "react";
import { Link } from "react-router-dom";
import { MdPerson, MdPersonAdd } from "react-icons/md";
import AlertPane from "components/alert";
import Badge from "components/badge/Badge";
import Button from "components/button";
import Dialog from "components/dialog";
import { formatDate, formatDateTime } from "utils/format";
import { getRespondent, profilePath } from "./respondent";

const formatAnswer = (answer) => (Array.isArray(answer) ? answer.join(", ") : String(answer));

/** Answers for one survey response, opened from the Responses tab or `?responseId=`. */
const ResponseDialog = ({ response, questions = [], addingPersonId, onAddPerson, onClose }) => {
  const respondent = response ? getRespondent(response, "Anonymous") : null;

  const renderPrimaryAction = () => {
    if (!response) return null;
    if (respondent.hasLinkedUser) {
      return (
        <Button as={Link} to={profilePath(response)} leftIcon={<MdPerson aria-hidden="true" />}>
          View Full Profile
        </Button>
      );
    }
    if (respondent.hasFollowUpInfo) {
      return (
        <Button
          loading={addingPersonId === response.id}
          leftIcon={<MdPersonAdd aria-hidden="true" />}
          onClick={() => {
            onAddPerson(response.id);
            onClose();
          }}
        >
          Add to People
        </Button>
      );
    }
    return null;
  };

  return (
    <Dialog
      isOpen={Boolean(response)}
      onClose={onClose}
      size="lg"
      title={respondent?.displayName}
      description={
        response && (
          <>
            {respondent.displayEmail && <span className="block">{respondent.displayEmail}</span>}
            <span className="block">
              Submitted {formatDateTime(response.submittedAt || response.createdAt)}
            </span>
          </>
        )
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          {renderPrimaryAction()}
        </>
      }
    >
      {response && (
        <div className="flex flex-col gap-4">
          <ol className="flex flex-col gap-4">
            {questions.map((question, index) => {
              const answer = response.answers?.[question.id];
              const hasAnswer = answer !== undefined && answer !== null && answer !== "";

              return (
                <li
                  key={question.id || index}
                  className="rounded-control border border-line p-4"
                >
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <span className="text-xs font-medium text-fg-muted">Question {index + 1}</span>
                    {question.required && (
                      <Badge tone="neutral" size="sm">
                        Required
                      </Badge>
                    )}
                  </div>
                  <p className="mb-3 font-medium text-fg">{question.text}</p>
                  <div className="rounded-control bg-surface-raised p-3">
                    {hasAnswer ? (
                      <p className="text-fg">{formatAnswer(answer)}</p>
                    ) : (
                      <p className="italic text-fg-muted">No answer provided</p>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>

          {(response.devotionalSubscription || response.followUpRequested) && (
            <section aria-labelledby="response-devotional-heading">
              <h3 id="response-devotional-heading" className="mb-3 text-sm font-semibold text-fg">
                Devotional Plan
              </h3>
              {response.devotionalSubscription ? (
                <AlertPane tone="success" title="Active Subscription" className="">
                  Day {response.devotionalSubscription.currentDay} of 30 • Started{" "}
                  {formatDate(response.devotionalSubscription.startDate)}
                </AlertPane>
              ) : (
                <AlertPane tone="warning" title="Follow-up Requested" className="">
                  This person requested to receive the 30-day devotional plan
                </AlertPane>
              )}
            </section>
          )}
        </div>
      )}
    </Dialog>
  );
};

export default ResponseDialog;
